import React, { useEffect, useRef, useState, useCallback } from 'react';

import { getThumbnailImageUrl } from '../../../../utils/getImageUrls';
import { TListingGallery } from 'types/TListing';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';

import Classes from './Thumbnails.module.scss';

type TThumbnailsProps = {
  imageUrls: TListingGallery['imageUrls'];
  imageSizes?: TListingGallery['media']['imageSizes'];
  activeImageIndex?: number;
  onChange: Function;
  loadAll?: boolean;
};

const HALF = 2;

const Thumbnails = ({ imageUrls, imageSizes, onChange, activeImageIndex = 0, loadAll = false }: TThumbnailsProps) => {
  const { t } = useI18nContext();
  const [activeThumbnail, setActiveThumbnail] = useState(activeImageIndex);
  const [loadAllImages, setLoadAllImages] = useState(loadAll);
  const thumbnailScrollElement = useRef<HTMLHeadingElement>(null);
  const thumbnailContainerElement = useRef<HTMLHeadingElement>(null);
  const maxVisibleImageCount = 5;

  const onClick = (thumbnailIndex: number) => {
    if (thumbnailIndex === activeThumbnail) {
      return;
    }
    setActiveThumbnail(thumbnailIndex);
    onChange(thumbnailIndex);
  };

  const showThumbnailInView = useCallback(
    (index: number) => {
      if (thumbnailScrollElement?.current && thumbnailContainerElement?.current) {
        const containerWidth = thumbnailContainerElement.current.clientWidth;
        const thumbnailWidth = thumbnailScrollElement.current.clientWidth / imageUrls.length;
        const halfPoint = containerWidth / HALF - thumbnailWidth / HALF;
        const activeElementPosition = halfPoint - thumbnailWidth * index;
        const max = thumbnailScrollElement.current.clientWidth - thumbnailContainerElement.current.clientWidth;
        const targetPosition = Math.min(Math.max(activeElementPosition, -max), 0);

        thumbnailScrollElement.current.style.transform = `translateX(${targetPosition}px)`;

        if (index > 0) {
          setLoadAllImages(true);
        }
      }
    },
    [imageUrls.length],
  );

  useEffect(() => {
    setActiveThumbnail(activeImageIndex);
    showThumbnailInView(activeImageIndex);
  }, [activeImageIndex, showThumbnailInView]);

  return (
    <div className={Classes.root}>
      <div ref={thumbnailContainerElement} className={Classes.cover}>
        <div ref={thumbnailScrollElement} className={Classes.scroll}>
          {imageUrls.map((imageUrl, index) => (
            <span
              role="img"
              onClick={() => onClick(index)}
              onFocus={() => onClick(index)}
              aria-label={t('gallery.carousel.thumbnail', { index: index + 1, total: imageUrls.length })}
              tabIndex={0}
              key={index}
              className={`${Classes.item} ${index === activeThumbnail ? Classes.active : ''}`}
              style={
                loadAllImages || index < maxVisibleImageCount
                  ? {
                      backgroundImage: `url("${getThumbnailImageUrl(imageUrl, imageSizes)}") `,
                    }
                  : {}
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Thumbnails;
